import { createSlotsContext } from '@grandvisionhq/slots';
import * as React from 'react';
import React__default, { useState, useMemo } from 'react';
import { useIntl, actions, reducer, useFeatureToggles, ClickAndReserveFeatureConfigEnum } from '@grandvisionhq/state';
import * as swcHelpers from '@swc/helpers';
import { placeholderImage, isString, sanitizeStringForUrl, formatPrice, useDynamicModule, useLocalStorage, isNil, isBoolean, isNumber, getLocalizedString, isModifiedClickEvent } from '@grandvisionhq/utils';
import { Button, Modal, Alert, List, MediaQuery, LockBodyScroll, toast, Price as Price$1, Loader, ProductTag, Sticky, Breadcrumbs } from '@grandvisionhq/common';
import { useStoreFinderContext, Hooks, Util, Components } from '@grandvisionhq/stores-v2';
import loadScript from 'load-script';
import classnames from 'classnames';
import { Heading, Text, RoundedButton, getSelectionBoxStyles, Radio, Hr } from '@grandvisionhq/elements';
import { CheckboxWithLabel } from '@grandvisionhq/forms';
import { Icon, FeatureIcon } from '@grandvisionhq/icons';
import { AddToCart } from '@grandvisionhq/carts-v2';
import { StockIndicator } from '@grandvisionhq/inventory';
import { ReviewSnippet, ReviewsProvider, ReviewDisplay } from '@grandvisionhq/reviews';
import { PromoteInstallmentsLabel } from '@grandvisionhq/content';
import { gql } from '@grandvisionhq/graphql';

const [ProductDetailSlotsProvider, useProductDetailSlots] = createSlotsContext({
    displayName: 'ProductDetailSlotsContext'
});

const getActiveVariant = (sku, product)=>{
    if (!sku) {
        return product.variants[0];
    }
    return product.variants.find((variant)=>variant.sku === sku
    ) || product.variants[0];
};

const getStockCount = (stockAvailability)=>{
    var ref;
    return (ref = stockAvailability === null || stockAvailability === void 0 ? void 0 : stockAvailability.availableQuantity) !== null && ref !== void 0 ? ref : 0;
};

const getTitle = (product)=>`${product.brand} ${product.brandModel} ${product.brandModelColorCode}`
;

const getImageUrl = (media, staticsUrl, isBynderImagesEnabled)=>{
    if (!media.images || !media.images.length) {
        return [
            placeholderImage
        ];
    }
    if (isBynderImagesEnabled) {
        return media.images.filter((image)=>image.label === 'Front'
        );
    }
    return media.images.map((image)=>{
        return {
            url: `${staticsUrl}${image.url}`,
            dimensions: swcHelpers.objectSpread({}, image.dimensions)
        };
    });
};

const getVariantDetailsLabel = (lensColor, frameColor)=>{
    var ref;
    const frameColors = (ref = frameColor.toString().replace(',', ', ')) !== null && ref !== void 0 ? ref : '';
    return [
        lensColor,
        frameColors
    ].filter((str)=>str.trim() !== ''
    ).join(' / ');
};

const getVariantDetails = (slug, variants, formatProductUrl, productType, isBynderImagesEnabled, staticsUrl)=>{
    const { locale  } = useIntl();
    return variants.map(({ sku , media , lensColor , frameColor  })=>{
        var ref;
        return {
            sku,
            image: (ref = getImageUrl(media, staticsUrl, isBynderImagesEnabled)[0]) === null || ref === void 0 ? void 0 : ref.url,
            url: formatProductUrl({
                productType,
                sku,
                slug: {
                    [locale]: slug
                }
            }),
            description: getVariantDetailsLabel(lensColor, frameColor)
        };
    });
};

const getPrice = (variant)=>{
    if (!variant) {
        console.error('No variant provided');
    }
    const price = variant.prices;
    if (!price) {
        console.error('Could not fetch the correct price');
    }
    return price;
};

const useBrandLink = (product, brand)=>{
    if (typeof window === 'undefined') {
        return '';
    }
    const link = React.useMemo(()=>{
        var ref;
        const brandName = product ? product === null || product === void 0 ? void 0 : product.brand : brand;
        if (!isString(brandName) || brand === '') return '';
        const sanitizedBrand = sanitizeStringForUrl(brandName);
        const path = ((ref = window.location) === null || ref === void 0 ? void 0 : ref.pathname) || '';
        const parts = path.split('/').filter((v)=>v !== ''
        );
        const isMultilingual = /[[a-z]{2}_[A-Z]{2}/g.test(parts[0]);
        const category = isMultilingual ? parts[1] : parts[0];
        const baseUrl = `/${isMultilingual ? `${parts[0]}/` : ''}${category}`;
        return `${baseUrl}/${encodeURIComponent(sanitizedBrand)}`;
    }, [
        brand,
        product
    ]);
    return link;
};

var DiscountType;
(function(DiscountType) {
    DiscountType["RELATIVE"] = "RELATIVE";
    DiscountType["ABSOLUTE"] = "ABSOLUTE";
})(DiscountType || (DiscountType = {}));
var ChannelAvailability;
(function(ChannelAvailability) {
    ChannelAvailability["ONLINE"] = "ONLINE";
    ChannelAvailability["IN_STORE_ONLY"] = "IN_STORE_ONLY";
})(ChannelAvailability || (ChannelAvailability = {}));

const useDiscountLabel = (value, type, currency, locale)=>{
    if (type === DiscountType.RELATIVE) {
        return `-${value}%`;
    }
    if (type === DiscountType.ABSOLUTE && !!locale) {
        return formatPrice(locale, value, currency, false);
    }
};

const updateActiveVariant = actions.action('UPDATE_ACTIVE_VARIANT', actions.payload());

const initialState = {
    sku: ''
};
const productDetailContextReducer = reducer(initialState, actions.on(updateActiveVariant, (state, action)=>swcHelpers.objectSpread({}, state, {
        sku: action.payload.sku
    })
));

/**
 * Product Context
 */ const ProductContext = /*#__PURE__*/ React.createContext({});
ProductContext.displayName = 'ProductContext';
const useProductData = ()=>{
    const context = React.useContext(ProductContext);
    if (!context) {
        throw new Error(`No ${ProductContext.displayName} available. You might need to add the ${ProductContext.displayName} provider component.`);
    }
    return context;
};
/**
 * Product Provider
 */ const ProductProvider = ({ children , formatProductUrl , product , productType , reviewsProvider , sku , slug , staticsUrl , specifications , stockResolver , productUrl , routes ,  })=>{
    const [state, dispatch] = React.useReducer(productDetailContextReducer, swcHelpers.objectSpread({}, initialState, {
        sku
    }));
    // TODO: move this mapping over to the mapGraphProductToClientProduct function
    const { brand  } = product;
    const activeVariant = getActiveVariant(state.sku, product);
    const brandUrl = useBrandLink(product, brand);
    const stockCount = getStockCount(activeVariant.stockAvailability);
    const title = getTitle({
        brand,
        brandModel: activeVariant.brandModel,
        brandModelColorCode: activeVariant.brandModelColorCode
    });
    const value = {
        activeSku: state.sku,
        activeVariant,
        brand,
        brandUrl,
        dispatch,
        formatProductUrl,
        product,
        productType,
        reviewsProvider,
        specifications,
        slug,
        staticsUrl,
        stockCount,
        stockResolver,
        productUrl,
        title,
        routes
    };
    return /*#__PURE__*/ React.createElement(ProductContext.Provider, {
        value: value
    }, children);
};

const CmsContext = React.createContext(null);
CmsContext.displayName = 'CmsContext';
const useCmsData = ()=>{
    const context = React.useContext(CmsContext);
    if (!context) {
        throw new Error(`No ${CmsContext.displayName} available. You might need to add the ${CmsContext.displayName} provider component.`);
    }
    return context;
};

function CmsProvider({ brandPromotions , usps , rxGlassesConfiguratorDocument , children  }) {
    const value = {
        brandPromotions,
        usps,
        rxGlassesConfiguratorDocument
    };
    return /*#__PURE__*/ React.createElement(CmsContext.Provider, {
        value: value
    }, children);
}

const ClickAndReserveButtonContainer = ({ onClick  })=>{
    const { getLabel  } = useIntl();
    return /*#__PURE__*/ React__default.createElement(Button, {
        caption: getLabel('products.actions.checkStoreAvailability'),
        variant: "ghost",
        icon: "store",
        color: "primary",
        onClick: onClick
    });
};

const ClickAndReserveModalContainer = ({ isOpen , onClose , children  })=>{
    const { getLabel  } = useIntl();
    return /*#__PURE__*/ React__default.createElement(Modal, {
        className: "click-and-reserve__modal",
        isOpen: isOpen,
        onModalClose: onClose,
        title: getLabel('products.clickAndReserve.modal.title'),
        showIcon: true,
        isFullScreen: true
    }, children);
};

const { StoreFinderItem  } = Components;
const ClickAndReserveStoreFinderItem = (_param)=>{
    var { store , onClick , variant  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "store",
        "onClick",
        "variant"
    ]);
    const { setHoveredStore  } = useStoreFinderContext();
    const { additionalStreetInfo , streetName , streetNumber , postalCode , town , province  } = store !== null && store !== void 0 ? store : {};
    const addressData = React__default.useMemo(()=>({
            additionalStreetInfo,
            streetName,
            streetNumber,
            postalCode,
            town,
            province
        })
    , [
        additionalStreetInfo,
        streetName,
        streetNumber,
        postalCode,
        town,
        province
    ]);
    var ref;
    const distance = Hooks.useFormattedDistance((ref = store === null || store === void 0 ? void 0 : store.distance) !== null && ref !== void 0 ? ref : undefined);
    // TODO: Get dynamic slug from Bloomreach routes
    const storeDetailUrlProvider = Util.getStoreDetailUrl('/stores');
    const storeDetailsUrl = store ? storeDetailUrlProvider(store) : undefined;
    const onClickHandler = React__default.useCallback(()=>{
        if (!storeDetailsUrl) {
            console.warn('no stores url was found, can not redirect');
            return;
        }
        store && window.location.assign(storeDetailsUrl);
    }, [
        store,
        storeDetailsUrl
    ]);
    const onMouseEnterHandler = React__default.useCallback(()=>{
        if (variant === 'map-item' || store === undefined) {
            return;
        }
        setHoveredStore(store);
    }, [
        setHoveredStore,
        store,
        variant
    ]);
    const onMouseLeaveHandler = React__default.useCallback(()=>{
        if (variant === 'map-item') {
            return;
        }
        setHoveredStore(null);
    }, [
        setHoveredStore,
        variant
    ]);
    if (!store) {
        return null;
    }
    return /*#__PURE__*/ React__default.createElement(StoreFinderItem, swcHelpers.extends({}, rest, {
        distance: distance,
        onClick: onClickHandler,
        addressData: addressData,
        addressSize: "COMPLETE",
        title: store.shortName,
        variant: variant,
        onMouseEnter: onMouseEnterHandler,
        onMouseLeave: onMouseLeaveHandler
    }));
};

const loadModule = function() {
    var _ref = swcHelpers.asyncToGenerator(function*() {
        const storeFinderModule = yield import(/* webpackChunkName: 'grandvisionhq-stores-v2' */ '@grandvisionhq/stores-v2');
        return storeFinderModule;
    });
    return function loadModule() {
        return _ref.apply(this, arguments);
    };
}();
const useStoreFinderModule = ()=>useDynamicModule(loadModule, '@grandvisionhq/stores-v2', [])
;
const ClickAndReserveStoreFinder = ()=>{
    const { getLabel  } = useIntl();
    const [module, error] = useStoreFinderModule();
    if (error || !module) {
        return /*#__PURE__*/ React__default.createElement(Alert, {
            type: "error",
            notifyScreenReader: false,
            message: getLabel('products.clickAndReserve.storeFinder.error')
        });
    }
    const { Containers , StoreFinderProvider , Components  } = module;
    const { StoreFinder  } = Containers;
    const { StoreListTopContent  } = Components;
    const listInfoContent = /*#__PURE__*/ React__default.createElement(List, {
        icon: "checkmark",
        items: [
            getLabel('products.clickAndReserve.storeFinder.usp1'),
            getLabel('products.clickAndReserve.storeFinder.usp2'),
            getLabel('products.clickAndReserve.storeFinder.usp3'), 
        ],
        className: "click-and-reserve__store-finder__usps"
    });
    return /*#__PURE__*/ React__default.createElement(StoreFinderProvider, {
        googleMapsId: "google-maps-container"
    }, /*#__PURE__*/ React__default.createElement(StoreFinder, {
        storeQueryForceDistance: true,
        isInsideModal: true,
        listTopContent: /*#__PURE__*/ React__default.createElement(StoreListTopContent, {
            title: getLabel('products.clickAndReserve.storeFinder.title'),
            description: getLabel('products.clickAndReserve.storeFinder.description')
        }),
        listInfoContent: listInfoContent,
        listItemComponent: (props)=>/*#__PURE__*/ React__default.createElement(ClickAndReserveStoreFinderItem, swcHelpers.extends({}, props, {
                variant: "list-item"
            }))
        ,
        infoWindowContentComponent: (props)=>/*#__PURE__*/ React__default.createElement(ClickAndReserveStoreFinderItem, swcHelpers.extends({}, props, {
                variant: "map-item"
            }))
    }));
};

const ClickAndReserveContainer = ()=>{
    const { featureConfigs , isClickAndReserveEnabled: isFeatureToggleEnabled  } = useFeatureToggles();
    const [isModalOpen, setModalOpen] = React__default.useState(false);
    const { activeVariant: { isReservable  } ,  } = useProductData();
    const handleButtonOnClick = React__default.useCallback(()=>{
        setModalOpen(true);
    }, [
        setModalOpen
    ]);
    const handleModalOnClose = React__default.useCallback(()=>{
        setModalOpen(false);
    }, [
        setModalOpen
    ]);
    var ref;
    const showClickAndReserveButton = isFeatureToggleEnabled && ((ref = featureConfigs === null || featureConfigs === void 0 ? void 0 : featureConfigs.clickAndReserve) !== null && ref !== void 0 ? ref : '') in ClickAndReserveFeatureConfigEnum && isReservable;
    if (!showClickAndReserveButton) return null;
    return /*#__PURE__*/ React__default.createElement("div", null, /*#__PURE__*/ React__default.createElement(ClickAndReserveButtonContainer, {
        onClick: handleButtonOnClick
    }), /*#__PURE__*/ React__default.createElement(ClickAndReserveModalContainer, {
        isOpen: isModalOpen,
        onClose: handleModalOnClose
    }, /*#__PURE__*/ React__default.createElement(ClickAndReserveStoreFinder, null)));
};

const VtoConfig = /*#__PURE__*/ React.createContext({
    isEnabled: false,
    isActive: false,
    setActive: ()=>null
});
const useVtoConfig = ()=>React.useContext(VtoConfig)
;

const TryOnButton = ({ variant ='outline' , onClick  })=>{
    const { isEnabled , isActive , setActive  } = useVtoConfig();
    const { getLabel  } = useIntl();
    const isPhone = MediaQuery.useIsPhone();
    const buttonSize = isPhone ? 'small' : 'medium';
    const startVTO = ()=>{
        setActive(true);
        onClick && onClick();
    };
    if (!isEnabled || isActive) return null;
    return /*#__PURE__*/ React.createElement(Button, {
        caption: getLabel('vto.callToAction'),
        variant: variant,
        icon: "try-on",
        color: "primary",
        size: buttonSize,
        onClick: startVTO
    });
};

function useDittoId(onDeleteCallback) {
    const vtoConfig = useVtoConfig();
    const [shouldSkipDeleteCallback, setSkipDeleteCallback] = useState(false);
    const [dittoId, setDittoId] = useLocalStorage('vto.dittoId', undefined, (value)=>value === undefined
    );
    const deleteDittoId = React__default.useCallback(swcHelpers.asyncToGenerator(function*(skipDeleteCallback = false) {
        setSkipDeleteCallback(skipDeleteCallback);
        if (!vtoConfig.isEnabled) {
            return;
        }
        if (dittoId) {
            setDittoId(undefined);
            const url = vtoConfig.apiUrl + `/api/1.3/dittos/${dittoId}`;
            const headers = {
                'X-Ditto-Access-Key-Id': vtoConfig.accessKey,
                'X-Ditto-Signature': yield vtoConfig.getSignature(dittoId)
            };
            yield fetch(url, {
                headers,
                method: 'delete'
            });
        }
    }), [
        dittoId
    ]);
    const idRef = React__default.useRef();
    React__default.useEffect(()=>{
        if (!shouldSkipDeleteCallback && onDeleteCallback && dittoId === undefined && (idRef === null || idRef === void 0 ? void 0 : idRef.current) !== undefined) {
            onDeleteCallback();
        }
        idRef.current = dittoId;
    }, [
        dittoId
    ]);
    return {
        dittoId,
        setDittoId,
        deleteDittoId
    };
}

const dittoSDKVersion = '5.0.1';
function loadDitto(sdkUrl) {
    return window.Ditto || new Promise((resolve, reject)=>loadScript(sdkUrl, (err)=>err ? reject(err) : resolve(window.Ditto)
        )
    );
}
const dittoLocalesMap = {
    'nl-BE': 'nl-nl',
    'fr-BE': 'fr-fr',
    'en-IE': 'en-gb',
    'en-IO': 'pt-pt'
};
const getDittoLocale = (locale)=>{
    let dittoLocale = locale.toLowerCase();
    if (locale in dittoLocalesMap) {
        dittoLocale = dittoLocalesMap[locale];
    }
    return dittoLocale;
};
function createVtoServerConfig(publicRuntimeDittoConfig, locale) {
    const { apiUrl , partnerId , sdkUrl , accessKey , signatureRoute  } = publicRuntimeDittoConfig;
    const dittoLocale = getDittoLocale(locale);
    const composedSdkUrl = `${sdkUrl}/default/${dittoSDKVersion}/${dittoLocale}/api.js`;
    return {
        apiUrl,
        sdkUrl: composedSdkUrl,
        accessKey,
        partnerId,
        signatureRoute
    };
}
const vtoEvents = {
    'vto.view': ({ sku  })=>({
            eventAction: 'Available',
            eventLabel: `Product ${sku} - Available`,
            VTO: {
                configFlow: {
                    action: 'view step',
                    name: 'vto available',
                    id: '000'
                }
            }
        })
    ,
    'vto.creation.view.intro': ({ sku  })=>({
            eventAction: 'View',
            eventLabel: `Product ${sku} - Intro`,
            VTO: {
                configFlow: {
                    action: 'view step',
                    name: 'introduction',
                    id: '100'
                }
            }
        })
    ,
    'vto.creation.view.camera': ({ sku  })=>({
            eventAction: 'View',
            eventLabel: `Product ${sku} - Allow camera`,
            VTO: {
                configFlow: {
                    action: 'view step',
                    name: 'allow camera',
                    id: '200'
                }
            }
        })
    ,
    'vto.creation.view.creation': ({ sku  })=>({
            eventAction: 'View',
            eventLabel: `Product ${sku} - Creation`,
            VTO: {
                configFlow: {
                    action: 'view step',
                    name: 'creation',
                    id: '300'
                }
            }
        })
    ,
    'vto.creation.view.record': ({ sku  })=>({
            eventAction: 'View',
            eventLabel: `Product ${sku} - Record`,
            VTO: {
                configFlow: {
                    action: 'view step',
                    name: 'record',
                    id: '400'
                }
            }
        })
    ,
    'vto.creation.view.save': ({ sku  })=>({
            eventAction: 'View',
            eventLabel: `Product ${sku} - Save`,
            VTO: {
                configFlow: {
                    action: 'view step',
                    name: 'save',
                    id: '500'
                }
            }
        })
    ,
    'vto.creation.success': ({ sku  })=>({
            eventAction: 'View',
            eventLabel: `Product ${sku} - Complete`,
            VTO: {
                configFlow: {
                    action: 'view step',
                    name: 'complete',
                    id: '600'
                }
            }
        })
    ,
    'vto.creation.error': ({ sku , status , description  })=>({
            eventAction: 'Failure',
            eventLabel: `Product ${sku} - Creation: error ${status}, ${description || ''}`,
            VTO: {
                configFlow: {
                    action: 'error',
                    name: status == 'CAMERA_BLOCKED' ? 'camera access' : 'creation failed'
                }
            }
        })
    ,
    'vto.tryon.open': ({ sku  })=>({
            eventAction: 'Open',
            eventLabel: `Product ${sku}`,
            VTO: {
                configFlow: {
                    action: 'click',
                    name: 'open vto'
                }
            }
        })
    ,
    'vto.tryon.retake': ({ sku  })=>({
            eventAction: 'Retake',
            eventLabel: `Product ${sku}`,
            VTO: {
                configFlow: {
                    action: 'click',
                    name: 'retake vto'
                }
            }
        })
    ,
    'vto.tryon.delete': ({ sku  })=>({
            eventAction: 'Delete',
            eventLabel: `Product ${sku}`,
            VTO: {
                configFlow: {
                    action: 'click',
                    name: 'delete vto'
                }
            }
        })
    ,
    'vto.close': ({ sku  })=>({
            eventAction: 'Close',
            eventLabel: `Product ${sku}`,
            VTO: {
                configFlow: {
                    action: 'click',
                    name: 'close vto'
                }
            }
        })
};
const vtoEvent = (key, args)=>{
    if (!vtoEvents[key]) {
        console.warn(`cannot send event '${key}', key does not exist`);
        return;
    }
    const { eventAction , eventLabel , VTO  } = vtoEvents[key](args);
    if (window.dataLayer) {
        window.dataLayer.push({
            event: 'VTOevent',
            eventCategory: 'Virtual Try-On',
            eventAction,
            eventLabel,
            VTO
        });
    }
};

var VtoCreationStep;
(function(VtoCreationStep) {
    VtoCreationStep[VtoCreationStep["INTRO"] = 0] = "INTRO";
    VtoCreationStep[VtoCreationStep["CREATION"] = 1] = "CREATION";
})(VtoCreationStep || (VtoCreationStep = {}));
const CreateDitto = ({ onCreate , onCancel , onError , skipIntro , sku , vtoBanner ,  })=>{
    const vtoConfig = useVtoConfig();
    const intl = useIntl();
    const [hasAgreed, setAgreement] = React.useState(false);
    const [activeStep, setActiveStep] = React.useState(skipIntro ? VtoCreationStep.CREATION : VtoCreationStep.INTRO);
    const [showErrorMessage, setShowErrorMessage] = React.useState(false);
    const [errorCode, setErrorCode] = React.useState(null);
    const imgUrl = vtoBanner === null || vtoBanner === void 0 ? void 0 : vtoBanner.image.url;
    React.useEffect(()=>{
        switch(activeStep){
            case VtoCreationStep.INTRO:
                return vtoEvent('vto.creation.view.intro', {
                    sku
                });
            case VtoCreationStep.CREATION:
                return vtoEvent('vto.creation.view.creation', {
                    sku
                });
        }
    }, [
        activeStep
    ]);
    React.useEffect(()=>{
        let isCanceled = false;
        if (!vtoConfig.isEnabled || activeStep !== VtoCreationStep.CREATION) {
            return;
        }
        let instance;
        Promise.all([
            loadDitto(vtoConfig.sdkUrl),
            vtoConfig.getSignature(vtoConfig.partnerId)
        ]).then(([ditto, clientSignature])=>{
            if (!isCanceled) {
                instance = new ditto.Scan({
                    tryOnServer: vtoConfig.apiUrl,
                    domSelector: '.vto-create-ditto__target',
                    accessKey: vtoConfig.accessKey,
                    partnerSignature: clientSignature,
                    partnerId: vtoConfig.partnerId,
                    font: 'nunitosans'
                }, {
                    success: (response)=>{
                        vtoEvent('vto.creation.success', {
                            sku
                        });
                        onCreate(response.scanId);
                    },
                    close: onCancel,
                    progress: ({ status  })=>{
                        switch(status){
                            case 'INIT_CAMERA':
                                vtoEvent('vto.creation.view.camera', {
                                    sku
                                });
                                break;
                            case 'READY':
                                vtoEvent('vto.creation.view.creation', {
                                    sku
                                });
                                break;
                            case 'RECORD':
                                vtoEvent('vto.creation.view.record', {
                                    sku
                                });
                                break;
                            case 'UPLOAD_STARTED':
                                vtoEvent('vto.creation.view.save', {
                                    sku
                                });
                                break;
                            default:
                                return;
                        }
                    },
                    failure: ({ status , description  })=>{
                        setErrorCode(status);
                        vtoEvent('vto.creation.error', {
                            sku,
                            status,
                            description
                        });
                    }
                });
            }
        }).catch(onError);
        return ()=>{
            isCanceled = true;
            instance && instance.destroy();
        };
    }, [
        activeStep
    ]);
    const onButtonClick = ()=>{
        if (hasAgreed) {
            return setActiveStep(VtoCreationStep.CREATION);
        }
        setShowErrorMessage(true);
    };
    return /*#__PURE__*/ React.createElement(Modal, {
        activeTrap: !skipIntro,
        isOpen: true,
        className: classnames('vto-create-ditto__modal', {
            'vto-create-ditto__modal--image': imgUrl
        }),
        showIcon: activeStep === VtoCreationStep.CREATION && errorCode !== 'CAMERA_ACCESS_DENIED' ? false : true,
        onModalClose: onCancel
    }, /*#__PURE__*/ React.createElement(LockBodyScroll, null), /*#__PURE__*/ React.createElement("div", {
        className: "vto-create-ditto"
    }, activeStep === VtoCreationStep.INTRO && renderPrivacy(), activeStep === VtoCreationStep.CREATION && errorCode !== 'CAMERA_ACCESS_DENIED' && /*#__PURE__*/ React.createElement("div", {
        className: "vto-create-ditto__target"
    }), errorCode && errorCode === 'CAMERA_ACCESS_DENIED' && /*#__PURE__*/ React.createElement("div", {
        className: "vto-create-ditto__error-message"
    }, intl.getLabel('vto.errors.cameraAccessDenied'))));
    function renderPrivacy() {
        return /*#__PURE__*/ React.createElement(React.Fragment, null, imgUrl && /*#__PURE__*/ React.createElement("div", {
            className: "vto-create-ditto__aside",
            style: {
                backgroundImage: `url(${imgUrl})`
            }
        }), /*#__PURE__*/ React.createElement("div", {
            className: classnames('vto-create-ditto__main', {
                'vto-create-ditto__main--with-error': showErrorMessage && !hasAgreed
            })
        }, /*#__PURE__*/ React.createElement("div", {
            className: "vto-create-ditto__titles"
        }, /*#__PURE__*/ React.createElement(Heading, {
            variant: "small",
            className: "vto-create-ditto__title"
        }, intl.getLabel('vto.title')), /*#__PURE__*/ React.createElement(Heading, {
            variant: "smallest",
            className: "vto-create-ditto__subtitle"
        }, intl.getLabel('vto.subtitle'))), /*#__PURE__*/ React.createElement(List, {
            className: "vto-create-ditto__usps",
            items: [
                intl.getLabel('vto.step.1'),
                intl.getLabel('vto.step.2'),
                intl.getLabel('vto.step.3'),
                intl.getLabel('vto.step.4'), 
            ],
            numerical: true
        }), /*#__PURE__*/ React.createElement(Text, {
            className: "vto-create-ditto__description",
            color: "text-secondary",
            variant: "caption"
        }, intl.getLabel('vto.description')), /*#__PURE__*/ React.createElement(CheckboxWithLabel, {
            id: "vto-create-ditto-opt-in",
            className: "vto-create-ditto__opt-in",
            onChange: ()=>setAgreement(!hasAgreed)
            ,
            checked: hasAgreed,
            value: "vto-create-ditto",
            "data-t": "vto-create-ditto-checkbox",
            label: intl.getFormattedLabel('vto.optIn', {
                parseHTML: true
            }),
            error: showErrorMessage && !hasAgreed,
            errorMessage: showErrorMessage && !hasAgreed ? intl.getLabel('vto.errors.terms') : ''
        }), /*#__PURE__*/ React.createElement(Button, {
            caption: intl.getLabel('vto.actions.start'),
            onClick: onButtonClick,
            "data-t": "vto-create-ditto-button"
        })));
    }
};

const ViewDitto = ({ sku , dittoId , onError , onDelete , onRetake  })=>{
    const vtoConfig = useVtoConfig();
    React.useEffect(()=>{
        if (!vtoConfig.isEnabled || !dittoId) {
            return;
        }
        let instance;
        Promise.all([
            loadDitto(vtoConfig.sdkUrl),
            vtoConfig.getSignature(dittoId)
        ]).then(([ditto, signature])=>{
            let isStatusSuccess = false;
            instance = new ditto.Overlay({
                accessKey: vtoConfig.accessKey,
                domSelector: '.vto-view-ditto__target',
                font: 'nunitosans',
                glassesId: sku,
                overlaySignature: signature,
                partnerId: vtoConfig.partnerId,
                scanId: dittoId,
                tryOnServer: vtoConfig.apiUrl
            }, {
                success: ()=>{
                    isStatusSuccess = true;
                },
                failure: ()=>{
                    if (!isStatusSuccess) {
                        vtoEvent('vto.creation.error', {
                            sku
                        });
                        onRetake();
                    }
                }
            });
        }).catch(onError);
        return ()=>{
            instance && instance.destroy();
        };
    }, [
        dittoId,
        vtoConfig,
        sku
    ]);
    return /*#__PURE__*/ React.createElement("div", {
        className: "vto-view-ditto"
    }, /*#__PURE__*/ React.createElement("div", {
        className: "vto-view-ditto__target"
    }), /*#__PURE__*/ React.createElement("div", {
        className: "vto-view-ditto__actions"
    }, /*#__PURE__*/ React.createElement(RoundedButton, {
        variant: "solid",
        size: "small",
        className: "vto-view-ditto__action",
        onClick: ()=>{
            vtoEvent('vto.tryon.retake', {
                sku
            });
            onRetake();
        },
        "data-t": "vto-view-ditto-retake"
    }, /*#__PURE__*/ React.createElement(Icon, {
        name: "pencil"
    })), /*#__PURE__*/ React.createElement(RoundedButton, {
        variant: "solid",
        size: "small",
        className: "vto-view-ditto__action",
        onClick: ()=>{
            vtoEvent('vto.tryon.delete', {
                sku
            });
            onDelete();
        },
        "data-t": "vto-view-ditto-delete"
    }, /*#__PURE__*/ React.createElement(Icon, {
        name: "trash"
    }))));
};

const VirtualTryOn = ({ onClose , sku , vtoBanner  })=>{
    const [skipIntro, setSkipIntro] = React.useState(false);
    const { dittoId , setDittoId , deleteDittoId  } = useDittoId(onClose);
    React.useEffect(()=>{
        vtoEvent('vto.tryon.open', {
            sku
        });
        return ()=>vtoEvent('vto.close', {
                sku
            })
        ;
    }, []);
    return dittoId ? /*#__PURE__*/ React.createElement(ViewDitto, {
        dittoId: dittoId,
        onDelete: handleDelete,
        onError: handleError,
        onRetake: handleRetake,
        sku: sku
    }) : /*#__PURE__*/ React.createElement(CreateDitto, {
        onCreate: setDittoId,
        onCancel: onClose,
        onError: handleError,
        skipIntro: skipIntro,
        vtoBanner: vtoBanner,
        sku: sku
    });
    function handleRetake() {
        deleteDittoId(true);
        setSkipIntro(true);
    }
    function handleDelete() {
        deleteDittoId();
    }
    function handleError() {
        onClose();
        // @TODO implement error logging
        toast.failed('vto.errors.connection');
    }
};

const TileLayout = ({ title , subtitle , price , iconName , className  })=>{
    var ref;
    const resolvedPriceCentAmount = (price === null || price === void 0 ? void 0 : (ref = price.discounted) === null || ref === void 0 ? void 0 : ref.value.centAmount) || (price === null || price === void 0 ? void 0 : price.value.centAmount);
    const currencyCode = price === null || price === void 0 ? void 0 : price.value.currencyCode;
    return /*#__PURE__*/ React__default.createElement("div", {
        className: classnames('tile-layout', className)
    }, iconName && /*#__PURE__*/ React__default.createElement("div", {
        className: "tile-layout__aside"
    }, /*#__PURE__*/ React__default.createElement(Icon, {
        name: iconName,
        className: "tile-layout__icon"
    })), /*#__PURE__*/ React__default.createElement("div", {
        className: "tile-layout__main"
    }, /*#__PURE__*/ React__default.createElement(Text, {
        className: "tile-layout__title",
        variant: "caption-head"
    }, title), subtitle && /*#__PURE__*/ React__default.createElement(Text, {
        className: "tile-layout__subtitle",
        variant: "caption"
    }, subtitle), typeof resolvedPriceCentAmount === 'number' && currencyCode && /*#__PURE__*/ React__default.createElement(Text, {
        className: "tile-layout__price",
        variant: "caption"
    }, /*#__PURE__*/ React__default.createElement(Price$1, {
        value: resolvedPriceCentAmount,
        centAmount: true,
        currency: currencyCode
    }))));
};

const SelectionBoxRadio = (props)=>{
    const classNames = getSelectionBoxStyles({
        isSelected: props.checked,
        isDisabled: props.disabled,
        className: props.className
    });
    return /*#__PURE__*/ React__default.createElement(Radio, swcHelpers.extends({}, props, {
        className: classNames
    }));
};

const SunglassesRxableSelect = ({ className , items , selectedValue , onChange ,  })=>{
    return /*#__PURE__*/ React__default.createElement("fieldset", {
        className: classnames('sunglasses-rxable-select', className)
    }, items.map(({ value , label , iconName  })=>{
        return /*#__PURE__*/ React__default.createElement(SelectionBoxRadio, {
            key: value,
            name: "sunglasses-rxable-select",
            value: value,
            checked: selectedValue === value,
            onChange: ()=>onChange(value)
            ,
            "data-t": value
        }, /*#__PURE__*/ React__default.createElement(TileLayout, {
            title: label,
            iconName: iconName
        }));
    }));
};

const WITH_RX$1 = 'with-rx';
const WITHOUT_RX$1 = 'without-rx';

const SunglassesRxableSelectContainer = (props)=>{
    const { getLabel  } = useIntl();
    const radioButtonItems = React__default.useMemo(()=>[
            {
                value: WITHOUT_RX$1,
                label: getLabel('products.selectRxAble.withoutRx'),
                iconName: 'sunglasses'
            },
            {
                value: WITH_RX$1,
                label: getLabel('products.selectRxAble.withRx'),
                iconName: 'store-eye-test'
            }, 
        ]
    , [
        getLabel
    ]);
    return /*#__PURE__*/ React__default.createElement(SunglassesRxableSelect, swcHelpers.extends({
        items: radioButtonItems
    }, props));
};

const NonRxProductAddToCartButton = ({ activeSku , offeringSku , bundleLineItemSkus , onCTAClick ,  })=>{
    const { getLabel  } = useIntl();
    let lineItemDraft;
    if (!bundleLineItemSkus || bundleLineItemSkus.length === 0) {
        // Add a Regular product to cart
        const draft = {
            sku: activeSku,
            quantity: 1,
            type: 'regular'
        };
        lineItemDraft = draft;
    } else {
        // Add a Non-Rx Bundle product to cart
        const skus = offeringSku ? [
            offeringSku,
            ...bundleLineItemSkus
        ] : bundleLineItemSkus;
        const draft = {
            sku: activeSku,
            quantity: 1,
            type: 'non-prescription-bundle',
            bundleLineItems: skus.map((sku)=>({
                    sku,
                    quantity: 1
                })
            )
        };
        lineItemDraft = draft;
    }
    return /*#__PURE__*/ React.createElement(AddToCart, {
        caption: getLabel('products.actions.addToCart'),
        onSuccess: onCTAClick,
        lineItemDrafts: [
            lineItemDraft
        ]
    });
};

const NonRxProductCTA = ({ action , addToCartConfig , storesRoute , handleConversionEvent ,  })=>{
    const { getLabel  } = useIntl();
    return action === 'ADD_TO_CART' ? // Add to Cart button
    /*#__PURE__*/ React.createElement(NonRxProductAddToCartButton, swcHelpers.extends({}, addToCartConfig)) : // Find a Store button
    /*#__PURE__*/ React.createElement(Button, {
        onClick: handleConversionEvent,
        caption: getLabel('products.actions.excludedFromCheckout'),
        "data-t": "btn-excluded-from-checkout",
        href: storesRoute
    });
};

const RxGlassesCTA = ({ action , bookAppointmentRoute , onClickWhenOrderable , buttonRef , className , handleConversionEvent ,  })=>{
    const { getLabel  } = useIntl();
    return action === 'OPEN_CONFIGURATOR' ? // Open Configurator button
    /*#__PURE__*/ React.createElement(Button, {
        caption: getLabel('products.actions.openConfigurator'),
        "data-t": "btn-open-configurator",
        onClick: onClickWhenOrderable,
        btnRef: buttonRef,
        className: className
    }) : // Book Appointment button
    /*#__PURE__*/ React.createElement(Button, {
        caption: getLabel('products.actions.bookAppointment'),
        "data-t": "btn-book-examination",
        href: bookAppointmentRoute,
        className: className,
        onClick: handleConversionEvent
    });
};

const CTA = ({ isLoading , shouldOpenConfigurator , buttonRef , handleConversionEvent , handleConfiguratorOpen , onCTAClick  })=>{
    const { activeVariant: { channelAvailability , sku  } , productUrl , routes ,  } = useProductData();
    return /*#__PURE__*/ React__default.createElement(React__default.Fragment, null, isLoading ? /*#__PURE__*/ React__default.createElement(Loader, null) : shouldOpenConfigurator ? /*#__PURE__*/ React__default.createElement(RxGlassesCTA, {
        bookAppointmentRoute: routes['book-appointment'],
        action: channelAvailability === ChannelAvailability.ONLINE ? 'OPEN_CONFIGURATOR' : 'OPEN_CONFIGURATOR_UNAVAILABLE',
        buttonRef: buttonRef,
        handleConversionEvent: handleConversionEvent,
        onClickWhenOrderable: handleConfiguratorOpen
    }) : /*#__PURE__*/ React__default.createElement(NonRxProductCTA, {
        storesRoute: routes.stores,
        action: channelAvailability === ChannelAvailability.ONLINE ? 'ADD_TO_CART' : 'ADD_TO_CART_UNAVAILABLE',
        handleConversionEvent: handleConversionEvent,
        addToCartConfig: {
            activeSku: sku,
            variantUrl: productUrl,
            onCTAClick
        }
    }));
};

const Discount = ({ discount , isWide =false , label  })=>/*#__PURE__*/ React__default.createElement("div", {
        className: "product-block-discount"
    }, /*#__PURE__*/ React__default.createElement("div", {
        className: "product-block-discount__value"
    }, discount), label && /*#__PURE__*/ React__default.createElement("div", {
        className: classnames('product-block-discount__label', {
            'product-block-discount__label--wide': isWide
        })
    }, label))
;

function BrandPromotion({ brandPromotions =[] , brandName , isClickable , className  }) {
    const { getFormattedLabel  } = useIntl();
    const brand = brandPromotions.find(({ name  })=>name.toLowerCase() === brandName.toLowerCase()
    );
    // when no brand data render nothing
    if (!brand) return null;
    // get the correct imageUrl (clickable vs default)
    const imageUrl = isClickable ? brand.clickableLogo : brand.logo;
    if (!imageUrl) return null;
    const labelOptions = {
        hideLabelKey: true,
        defaultValue: `${brandName} reseller`,
        labelData: {
            brand: brandName
        }
    };
    const imageAlt = isClickable ? getFormattedLabel('products.brand.promotionLogo.verify', labelOptions) : getFormattedLabel('products.brand.promotionLogo', labelOptions);
    const image = /*#__PURE__*/ React__default.createElement("img", {
        src: imageUrl,
        alt: imageAlt,
        loading: "lazy"
    });
    return /*#__PURE__*/ React__default.createElement("div", {
        className: `brand-promotion ${className ? className : ''}`
    }, (brand === null || brand === void 0 ? void 0 : brand.url) && isClickable ? /*#__PURE__*/ React__default.createElement("a", {
        href: brand.url,
        target: "_blank",
        rel: "noreferrer"
    }, image) : /*#__PURE__*/ React__default.createElement(React__default.Fragment, null, image));
}

const ProductDetailTitle = ({ title , subtitle , href  })=>/*#__PURE__*/ React.createElement("div", {
        className: "product-detail-title"
    }, !!href ? /*#__PURE__*/ React.createElement("a", {
        href: href,
        className: "product-detail-title__link"
    }, /*#__PURE__*/ React.createElement("span", {
        className: "product-detail-title__subtitle"
    }, subtitle)) : /*#__PURE__*/ React.createElement("span", {
        className: "product-detail-title__subtitle"
    }, subtitle), /*#__PURE__*/ React.createElement(Heading, {
        variant: "small",
        component: "h1"
    }, title))
;

const WITH_RX = 'with-rx';
const WITHOUT_RX = 'without-rx';

const isRxRadioSelection = (value)=>value === WITH_RX || value === WITHOUT_RX
;

const CartDetails = ({ isVtoEnabled , isOfferedWithPrescriptions  })=>{
    const [selectWithRxLens, setSelectWithRxLens] = React__default.useState(WITHOUT_RX);
    const shouldOpenConfigurator = isOfferedWithPrescriptions && selectWithRxLens === WITH_RX;
    const openConfiguratorButtonRef = React__default.useRef(null);
    const [configuratorIsLoading] = React__default.useState(false);
    const onRxRadioChange = React__default.useCallback((newSelection)=>{
        if (isRxRadioSelection(newSelection) && selectWithRxLens !== newSelection) {
            setSelectWithRxLens(newSelection);
        // triggerDataLayerEvent('click on button', { isWithRx: newSelection === WITH_RX })
        }
    }, [
        selectWithRxLens
    ]);
    const handleConfiguratorOpen = React__default.useCallback(()=>{
    // @TODO: handle conversion event in #
    // Possibly we will need to add this to products-details-v2
    }, []);
    const handleConversionEvent = React__default.useCallback(()=>{
    // @TODO: handle conversion event in #
    // Possibly we will need to add this to products-details-v2
    }, []);
    const onCTAClick = React__default.useCallback(()=>{
    // @TODO: handle cta click in #
    // Possibly we will need to add this to products-details-v2
    }, []);
    return /*#__PURE__*/ React__default.createElement(React__default.Fragment, null, /*#__PURE__*/ React__default.createElement("div", {
        className: "product-detail__cart-details"
    }, isOfferedWithPrescriptions && /*#__PURE__*/ React__default.createElement(SunglassesRxableSelectContainer, {
        onChange: onRxRadioChange,
        selectedValue: selectWithRxLens
    }), /*#__PURE__*/ React__default.createElement(CTA, {
        isLoading: configuratorIsLoading,
        shouldOpenConfigurator: shouldOpenConfigurator,
        handleConfiguratorOpen: handleConfiguratorOpen,
        handleConversionEvent: handleConversionEvent,
        buttonRef: openConfiguratorButtonRef,
        onCTAClick: onCTAClick
    }), isVtoEnabled && /*#__PURE__*/ React__default.createElement(TryOnButton, null), /*#__PURE__*/ React__default.createElement(ClickAndReserveContainer, null)));
};

const ProductDetailPrice = ({ originalPrice , discountedPrice , currency , customSize , centAmount =true ,  })=>{
    if (originalPrice && discountedPrice && originalPrice !== discountedPrice) {
        return /*#__PURE__*/ React__default.createElement(React__default.Fragment, null, /*#__PURE__*/ React__default.createElement(Price$1, {
            value: discountedPrice,
            currency: currency,
            centAmount: centAmount,
            useDigits: !centAmount,
            className: "product-detail__price",
            size: customSize !== null && customSize !== void 0 ? customSize : 'large',
            type: "discount"
        }), /*#__PURE__*/ React__default.createElement(Price$1, {
            value: originalPrice,
            currency: currency,
            centAmount: centAmount,
            useDigits: !centAmount,
            className: "product-detail__price",
            size: customSize !== null && customSize !== void 0 ? customSize : 'large',
            type: "strikethrough"
        }));
    }
    return /*#__PURE__*/ React__default.createElement(Price$1, {
        value: originalPrice,
        currency: currency,
        centAmount: centAmount,
        useDigits: !centAmount,
        size: customSize !== null && customSize !== void 0 ? customSize : 'large',
        className: "product-detail__price"
    });
};

const ProductDiscountLabel = ({ title , modifier  })=>title ? /*#__PURE__*/ React.createElement("div", {
        className: classnames('product-discount-label', {
            [`product-discount-label--${modifier}`]: !!modifier
        })
    }, /*#__PURE__*/ React.createElement("span", {
        className: "product-discount-label__title"
    }, title)) : null
;

const getAttribute = (variant, attribute)=>{
    if (variant.hasOwnProperty(attribute)) {
        const key = attribute;
        return variant[key];
    }
};
function isProductAttribute(value) {
    return value.displayName !== undefined;
}
/**
 *
 * Helper for converting specification data to a string format
 *
 * if a value is an array, that value has to be converted to a string of those values. ('blue'. 'red')
 * if a value is a boolean, that value has to be converted to ('yes'/'no')
 * if a value is a number, that value has to be converted to a string of that number. (preventing things like 8.5e10)
 *
 */ const getSpecificationItem = (config, variant, getLabel)=>{
    const attr = getAttribute(variant, config.attribute);
    if (isNil(attr) || attr === '') {
        return;
    }
    let value = attr;
    if (isBoolean(attr)) {
        value = attr ? getLabel('common.yes') : getLabel('common.no');
    }
    if (isNumber(attr)) {
        value = attr.toString();
    }
    if (Array.isArray(value) && value.length > 0 && value.every((itemValue)=>typeof itemValue === 'string' || !itemValue
    )) {
        value = value.join(', ');
    }
    if (isProductAttribute(value)) {
        value = value.displayName;
    }
    // Final check for when something unexpected happend
    if (!isString(value)) {
        console.warn(`Could not render ${config.labelKey} because the received value is a ${typeof attr} while expecting a string`);
        return;
    }
    return {
        label: getLabel(config.labelKey),
        value
    };
};

const SpecificationItem = ({ config , variant  })=>{
    const { getLabel  } = useIntl();
    const item = getSpecificationItem(config, variant, getLabel);
    if (!item) {
        return null;
    }
    return /*#__PURE__*/ React.createElement("div", {
        className: "product-detail-specification__item",
        "data-t": "specification-item",
        role: "presentation"
    }, /*#__PURE__*/ React.createElement("dt", {
        className: "product-detail-specification__item--label"
    }, item.label), /*#__PURE__*/ React.createElement("dd", {
        className: "product-detail-specification__item--value"
    }, item.value));
};

const ProductDetailSpecification = ({ configuration , variant  })=>{
    const { getLabel  } = useIntl();
    const title = getLabel('products.detail.specifications.title');
    if (!configuration || !variant) {
        return null;
    }
    const items = configuration.map((config)=>/*#__PURE__*/ React.createElement(SpecificationItem, {
            config: config,
            variant: variant,
            key: config.labelKey
        })
    );
    if (!items.length) {
        return null;
    }
    return /*#__PURE__*/ React.createElement("section", {
        className: "product-detail-specification",
        "data-t": "product-detail-specification"
    }, /*#__PURE__*/ React.createElement(Heading, {
        variant: "smallest",
        className: "product-detail-specification__title"
    }, title), /*#__PURE__*/ React.createElement("dl", {
        className: "product-detail-specification__list"
    }, items));
};

const ProductDetailDescription = ({ children , description , header , title , usps ,  })=>/*#__PURE__*/ React.createElement("section", {
        className: "product-detail-description",
        "aria-label": title,
        "data-t": "product-detail-description"
    }, title && /*#__PURE__*/ React.createElement(Heading, {
        variant: "smallest",
        component: "h2"
    }, title), usps, header && /*#__PURE__*/ React.createElement(Heading, {
        variant: "subheading1",
        className: "product-detail-description__subheading",
        component: "h3"
    }, header), description && /*#__PURE__*/ React.createElement(Text, {
        variant: "body"
    }, description), children)
;

const isEnabled = (item)=>{
    if (typeof item === 'string') {
        return parseInt(item, 10) > 0;
    }
    return !!item;
};
const ProductDetailFeatures = ({ uvCategory , polarised , glazable  })=>{
    const { getLabel  } = useIntl();
    return /*#__PURE__*/ React.createElement("ul", {
        className: "product-detail-features"
    }, isEnabled(uvCategory) && /*#__PURE__*/ React.createElement("li", {
        className: "product-detail-features__feature product-detail-features__feature--uv"
    }, /*#__PURE__*/ React.createElement(FeatureIcon, {
        name: "uv",
        color: "orange",
        size: 38
    }), getLabel('products.sunglasses.features.uvCategory')), isEnabled(polarised) && /*#__PURE__*/ React.createElement("li", {
        className: "product-detail-features__feature product-detail-features__feature--polarised"
    }, /*#__PURE__*/ React.createElement(FeatureIcon, {
        name: "polarized",
        color: "orange",
        size: 38
    }), getLabel('products.sunglasses.features.polarized')), isEnabled(glazable) && /*#__PURE__*/ React.createElement("li", {
        className: "product-detail-features__feature product-detail-features__feature--glazable"
    }, /*#__PURE__*/ React.createElement(FeatureIcon, {
        name: "reading",
        color: "orange",
        size: 38
    }), getLabel('products.sunglasses.features.glazeable')));
};

const ProductDetailUspItem = ({ item  })=>{
    const { icon , text  } = item;
    return /*#__PURE__*/ React.createElement("div", {
        className: "product-detail-usps__item"
    }, icon && /*#__PURE__*/ React.createElement("span", {
        className: "product-detail-usps__icon"
    }, /*#__PURE__*/ React.createElement(Icon, {
        name: icon,
        width: 24,
        height: 24
    })), /*#__PURE__*/ React.createElement("span", {
        className: "product-detail-usps__message"
    }, text));
};

const ProductDetailUsps = ({ items , layout ='list' , gridColumns =3 , className ,  })=>{
    if (gridColumns < 2 || gridColumns > 5) {
        console.warn('ProductDetailUsps only supports between 2 and 5 columns (inclusive) in grid mode');
    }
    return /*#__PURE__*/ React.createElement("div", {
        className: classnames('product-detail-usps', `product-detail-usps--layout-${layout}`, className, {
            [`product-detail-usps--columns-${gridColumns}`]: layout === 'grid'
        })
    }, items.map((usp)=>/*#__PURE__*/ React.createElement(ProductDetailUspItem, {
            item: usp,
            key: usp.text
        })
    ));
};

const ProductInfo = ()=>{
    const { brand , activeVariant , product: { description  } , specifications ,  } = useProductData();
    const { header , isPolarized , isGlazeble , uvRaysProtection , usps  } = activeVariant;
    const shouldShowProductFeatures = !!isPolarized || !!isGlazeble || !!uvRaysProtection;
    const { brandPromotions  } = useCmsData();
    const { getLabel  } = useIntl();
    return /*#__PURE__*/ React__default.createElement(React__default.Fragment, null, /*#__PURE__*/ React__default.createElement("div", {
        className: "product-detail__description"
    }, /*#__PURE__*/ React__default.createElement(ProductDetailDescription, {
        // TODO: replace this label with something more generic
        title: getLabel('products.contactLenses.header.details'),
        header: header,
        description: description,
        usps: /*#__PURE__*/ React__default.createElement(List, {
            icon: "checkmark",
            items: usps
        })
    }, shouldShowProductFeatures && /*#__PURE__*/ React__default.createElement(ProductDetailFeatures, {
        uvCategory: uvRaysProtection,
        polarised: isPolarized,
        glazable: isGlazeble
    }), /*#__PURE__*/ React__default.createElement(BrandPromotion, {
        brandPromotions: brandPromotions,
        brandName: brand,
        className: "product-detail__brand-promotion--clickable",
        isClickable: true
    }))), /*#__PURE__*/ React__default.createElement("div", {
        className: "product-detail__specification"
    }, /*#__PURE__*/ React__default.createElement(ProductDetailSpecification, {
        configuration: specifications,
        variant: activeVariant
    })));
};

const VtoConfigProvider = ({ config , children  })=>{
    const { locale  } = useIntl();
    const { isVtoEnabled: enabled  } = useFeatureToggles();
    const [isActive, setActive] = React.useState(false);
    const configValue = React.useMemo(()=>{
        if (enabled && config) {
            const serverConfig = createVtoServerConfig(config, locale);
            const getSignature = function() {
                var _ref = swcHelpers.asyncToGenerator(function*(message) {
                    return fetch(`${serverConfig.signatureRoute}?message=${message}`).then((response)=>response.json()
                    ).then(({ signature  })=>signature
                    );
                });
                return function getSignature(message) {
                    return _ref.apply(this, arguments);
                };
            }();
            return swcHelpers.objectSpread({}, serverConfig, {
                getSignature,
                isEnabled: true
            });
        }
        return {
            isEnabled: false
        };
    }, [
        config,
        enabled,
        locale
    ]);
    const value = swcHelpers.objectSpread({}, configValue, {
        isActive,
        setActive
    });
    return /*#__PURE__*/ React.createElement(VtoConfig.Provider, {
        value: value
    }, children);
};

const Price = ({ price  })=>{
    var ref, ref1, ref2, ref3, ref4, ref5, ref6, ref7, ref8, ref9, ref10, ref11, ref12, ref13, ref14, ref15, ref16;
    const { getLabel , locale  } = useIntl();
    const { isProductLabelEnabled  } = useFeatureToggles();
    const { Slots  } = useProductDetailSlots();
    const discount = useDiscountLabel((ref = price.discountedPrice) === null || ref === void 0 ? void 0 : (ref1 = ref.discountInformation) === null || ref1 === void 0 ? void 0 : ref1.value, (ref2 = price.discountedPrice) === null || ref2 === void 0 ? void 0 : (ref3 = ref2.discountInformation) === null || ref3 === void 0 ? void 0 : ref3.type, (ref4 = price.discountedPrice) === null || ref4 === void 0 ? void 0 : (ref5 = ref4.price) === null || ref5 === void 0 ? void 0 : ref5.currencyCode, locale);
    const discountIsZero = ((ref6 = price.discountedPrice) === null || ref6 === void 0 ? void 0 : (ref7 = ref6.price) === null || ref7 === void 0 ? void 0 : ref7.amount) === ((ref8 = price.originalPrice) === null || ref8 === void 0 ? void 0 : ref8.amount);
    return /*#__PURE__*/ React__default.createElement(React__default.Fragment, null, /*#__PURE__*/ React__default.createElement("div", {
        className: classnames('product-detail__specification-prices', {
            'product-detail__specification-prices--has-discount': !!price.discountedPrice
        }),
        role: "group",
        "aria-label": getLabel('products.screenreader.priceInformation')
    }, price.originalPrice && /*#__PURE__*/ React__default.createElement(React__default.Fragment, null, /*#__PURE__*/ React__default.createElement("div", {
        className: "product-detail__price-container"
    }, /*#__PURE__*/ React__default.createElement(ProductDetailPrice, {
        centAmount: false,
        originalPrice: (ref9 = price.originalPrice) === null || ref9 === void 0 ? void 0 : ref9.amount,
        discountedPrice: (ref10 = price.discountedPrice) === null || ref10 === void 0 ? void 0 : (ref11 = ref10.price) === null || ref11 === void 0 ? void 0 : ref11.amount,
        currency: (ref12 = price.originalPrice) === null || ref12 === void 0 ? void 0 : ref12.currencyCode
    })), !!isProductLabelEnabled && /*#__PURE__*/ React__default.createElement(ProductDiscountLabel, {
        title: getLocalizedString((ref13 = price.discountedPrice) === null || ref13 === void 0 ? void 0 : (ref14 = ref13.discountInformation) === null || ref14 === void 0 ? void 0 : ref14.name, locale)
    }), !isProductLabelEnabled && !!discount && !discountIsZero && /*#__PURE__*/ React__default.createElement(Discount, {
        discount: discount,
        label: getLocalizedString((ref15 = price.discountedPrice) === null || ref15 === void 0 ? void 0 : (ref16 = ref15.discountInformation) === null || ref16 === void 0 ? void 0 : ref16.description, locale),
        isWide: true
    }))), Slots.AfterPrice ? /*#__PURE__*/ React__default.createElement("div", {
        className: "product-detail__after-price-slot"
    }, /*#__PURE__*/ React__default.createElement(Slots.AfterPrice, null)) : null);
};

const imgSuffix = '?w=64&fm=jpeg&auto=format';
const VariantPicker = ({ title , variants , currentSku , currentLabel , stockIndicator , onPick  })=>{
    const [queryString, setQueryString] = React__default.useState('');
    const { getLabel  } = useIntl();
    // will pass the current queryString in the url of the variant picker
    React__default.useEffect(()=>{
        setQueryString(typeof window !== 'undefined' ? window.location.search : '');
    }, []);
    if (!variants.length) {
        return null;
    }
    if (variants.length === 1) {
        return /*#__PURE__*/ React__default.createElement("div", {
            className: "variant-picker"
        }, /*#__PURE__*/ React__default.createElement("div", {
            className: "variant-picker__header"
        }, /*#__PURE__*/ React__default.createElement("div", {
            className: "variant-picker__info"
        }, currentLabel), stockIndicator));
    }
    return /*#__PURE__*/ React__default.createElement("div", {
        className: "variant-picker"
    }, /*#__PURE__*/ React__default.createElement("div", {
        className: "variant-picker__header"
    }, /*#__PURE__*/ React__default.createElement("strong", null, title), stockIndicator), /*#__PURE__*/ React__default.createElement("div", {
        className: "variant-picker__info"
    }, currentLabel), /*#__PURE__*/ React__default.createElement("div", {
        className: "variant-picker__variants"
    }, variants.map(({ sku , image , url , description  })=>{
        const accessibleName = description || `${sku} - ${getLabel('products.variant.description.screenreader.fallback')}`;
        return /*#__PURE__*/ React__default.createElement("a", {
            href: url && `${url}${queryString}`,
            "data-t": "variant-picker-button",
            key: sku,
            title: accessibleName,
            className: classnames('variant-picker__variant', {
                'variant-picker__variant--active': currentSku === sku
            }),
            onClick: (event)=>onPick(event, sku)
        }, /*#__PURE__*/ React__default.createElement("img", {
            src: `${image}${imgSuffix}`,
            alt: accessibleName
        }));
    })));
};

const Variants = ()=>{
    const { activeVariant , brand , dispatch , formatProductUrl , product: { variants  } , productType , slug , staticsUrl , stockCount , stockResolver ,  } = useProductData();
    const { isAllowBackOrder , sku: sku1 , isExcludedFromCheckout , lensColor , frameColor  } = activeVariant;
    const { getFormattedLabel , locale  } = useIntl();
    const { isBynderImagesEnabled  } = useFeatureToggles();
    const title = getFormattedLabel('products.numberOfVariants', {
        labelData: {
            number: variants.length
        }
    });
    const currentLabel = getVariantDetailsLabel(lensColor, frameColor);
    const handleVariantPick = React__default.useCallback((event, sku)=>{
        dispatch(updateActiveVariant({
            sku
        }));
        const newUrl = formatProductUrl({
            sku,
            slug: {
                [locale]: slug
            },
            productType
        });
        // will pass the current queryString in the url of the variant picker
        const queryString = typeof window !== 'undefined' ? window.location.search : '';
        window.history.replaceState(null, brand || '', `${newUrl}${queryString}`);
        // Allow customers to open another variant in a new tab or window, otherwise prevent navigation
        if (event && !isModifiedClickEvent(event)) {
            event.preventDefault();
        }
    }, [
        brand,
        dispatch,
        formatProductUrl,
        slug,
        productType,
        locale
    ]);
    return /*#__PURE__*/ React__default.createElement(VariantPicker, {
        title: title,
        variants: getVariantDetails(slug, variants, formatProductUrl, productType, isBynderImagesEnabled, staticsUrl),
        currentSku: sku1,
        currentLabel: currentLabel,
        onPick: handleVariantPick,
        stockIndicator: !isExcludedFromCheckout && /*#__PURE__*/ React__default.createElement(StockIndicator, {
            stockCount: stockCount,
            allowBackOrder: isAllowBackOrder,
            stockResolver: stockResolver
        })
    });
};

const PowerReviews = ({ sku  })=>/*#__PURE__*/ React__default.createElement(ReviewSnippet, {
        productId: sku,
        variantId: sku
    })
;

const Tags = ({ tags  })=>{
    const { isProductLabelEnabled  } = useFeatureToggles();
    if (!(tags === null || tags === void 0 ? void 0 : tags.length) || tags[0].value === '' || isProductLabelEnabled) return null;
    return /*#__PURE__*/ React__default.createElement("div", {
        className: "product-detail__product-tag"
    }, /*#__PURE__*/ React__default.createElement(ProductTag, {
        tag: tags[0]
    }));
};

// This is going to be unified template for all product types
const ProductTemplate = ()=>{
    const { activeVariant , brand , product , title , brandUrl  } = useProductData();
    const { isOfferedWithPrescriptions  } = product;
    const { brandModel , isExcludedFromCheckout , isVtoEnabled , tags , sku  } = activeVariant;
    const { brandPromotions , usps  } = useCmsData();
    const isMobile = MediaQuery.useIsBelow(MediaQuery.BreakpointName.TABLET_LANDSCAPE);
    const price = getPrice(activeVariant);
    return /*#__PURE__*/ React__default.createElement("div", {
        className: "product-detail__main"
    }, /*#__PURE__*/ React__default.createElement("div", {
        className: "desktop-hidden product-detail__header--mobile"
    }, /*#__PURE__*/ React__default.createElement(ProductDetailTitle, {
        title: title,
        subtitle: product.brand,
        href: brandUrl
    }), /*#__PURE__*/ React__default.createElement(BrandPromotion, {
        brandPromotions: brandPromotions,
        brandName: brand,
        className: "product-detail__brand-promotion"
    })), /*#__PURE__*/ React__default.createElement("div", {
        className: "product-detail__image-gallery"
    }, "product-detail__image-gallery"), /*#__PURE__*/ React__default.createElement(ProductInfo, null), /*#__PURE__*/ React__default.createElement("section", {
        className: "product-detail__aside",
        "aria-label": `${brand} ${brandModel}`
    }, /*#__PURE__*/ React__default.createElement(Sticky, {
        top: 0,
        bottomBoundary: '.product-detail',
        enabled: !isMobile
    }, /*#__PURE__*/ React__default.createElement("div", {
        className: "mobile-hidden"
    }, /*#__PURE__*/ React__default.createElement(ProductDetailTitle, {
        title: title,
        subtitle: product.brand,
        href: brandUrl
    }), /*#__PURE__*/ React__default.createElement(BrandPromotion, {
        brandPromotions: brandPromotions,
        brandName: brand,
        className: "product-detail__brand-promotion"
    }), isMobile && /*#__PURE__*/ React__default.createElement(PowerReviews, {
        sku: sku
    })), /*#__PURE__*/ React__default.createElement(Tags, {
        tags: tags
    }), /*#__PURE__*/ React__default.createElement(Price, {
        price: price
    }), !isMobile && /*#__PURE__*/ React__default.createElement(PowerReviews, {
        sku: sku
    }), /*#__PURE__*/ React__default.createElement(Variants, null), /*#__PURE__*/ React__default.createElement(CartDetails, {
        isVtoEnabled: isVtoEnabled,
        isOfferedWithPrescriptions: isOfferedWithPrescriptions
    }), (!isExcludedFromCheckout || usps) && /*#__PURE__*/ React__default.createElement(Hr, {
        className: "product-detail__usp-divider"
    }), usps && /*#__PURE__*/ React__default.createElement(ProductDetailUsps, {
        items: usps
    }))));
};

const PromoteInstallmentsModalSlot = ({ siteLogos , price , numberOfInstallments  })=>{
    const { getLabel , currency , locale  } = useIntl();
    const monthsIndexArray = useMemo(()=>{
        return Array(numberOfInstallments - 1).fill(0).map((_value, index)=>index + 1
        );
    }, [
        numberOfInstallments
    ]);
    if (numberOfInstallments <= 1) {
        return null;
    }
    const logo = /*#__PURE__*/ React__default.createElement("img", {
        src: siteLogos.payInstallmentsLogo,
        alt: getLabel('installments.modal.logoAlt')
    });
    const pricePerMonthValue = Math.floor(price / numberOfInstallments);
    // First month can be slightly different due to rounding errors. Make sure it all adds up to the total price
    const priceNow = formatPrice(locale, price - (numberOfInstallments - 1) * pricePerMonthValue, currency, true);
    const pricePerMonth = formatPrice(locale, pricePerMonthValue, currency, true);
    return /*#__PURE__*/ React__default.createElement("div", {
        className: "promote-installments-modal-slot"
    }, /*#__PURE__*/ React__default.createElement("div", {
        className: "promote-installments-modal-slot__intro"
    }, /*#__PURE__*/ React__default.createElement("p", null, getLabel('installments.modal.partnerDescription'), " ", logo)), /*#__PURE__*/ React__default.createElement("div", {
        className: "promote-installments-modal-slot__steps-wrapper"
    }, /*#__PURE__*/ React__default.createElement("p", null, getLabel('installments.modal.stepsTitle')), /*#__PURE__*/ React__default.createElement("div", {
        className: "promote-installments-modal-slot__steps"
    }, /*#__PURE__*/ React__default.createElement("div", {
        className: "promote-installments-modal-slot__step"
    }, /*#__PURE__*/ React__default.createElement("div", {
        className: "promote-installments-modal-slot__step-number"
    }, "1"), /*#__PURE__*/ React__default.createElement("div", {
        className: "promote-installments-modal-slot__step-description"
    }, getLabel('installments.modal.step1'))), /*#__PURE__*/ React__default.createElement("div", {
        className: "promote-installments-modal-slot__step"
    }, /*#__PURE__*/ React__default.createElement("div", {
        className: "promote-installments-modal-slot__step-number"
    }, "2"), /*#__PURE__*/ React__default.createElement("div", {
        className: "promote-installments-modal-slot__step-description"
    }, getLabel('installments.modal.step2')), logo && /*#__PURE__*/ React__default.createElement("div", {
        className: "promote-installments-modal-slot__step-logo"
    }, logo)), /*#__PURE__*/ React__default.createElement("div", {
        className: "promote-installments-modal-slot__step"
    }, /*#__PURE__*/ React__default.createElement("div", {
        className: "promote-installments-modal-slot__step-number"
    }, "3"), /*#__PURE__*/ React__default.createElement("div", {
        className: "promote-installments-modal-slot__step-description"
    }, getLabel('installments.modal.step3'))), /*#__PURE__*/ React__default.createElement("div", {
        className: "promote-installments-modal-slot__step"
    }, /*#__PURE__*/ React__default.createElement("div", {
        className: "promote-installments-modal-slot__step-number"
    }, "4"), /*#__PURE__*/ React__default.createElement("div", {
        className: "promote-installments-modal-slot__step-description"
    }, getLabel('installments.modal.step4'))))), /*#__PURE__*/ React__default.createElement("div", {
        className: "promote-installments-modal-slot__pay"
    }, /*#__PURE__*/ React__default.createElement("div", {
        className: "promote-installments-modal-slot__pay-now"
    }, /*#__PURE__*/ React__default.createElement("div", null, getLabel('installments.modal.payNow')), /*#__PURE__*/ React__default.createElement("div", null, priceNow, " ", getLabel('installments.modal.payNowMethod')), /*#__PURE__*/ React__default.createElement("br", null), /*#__PURE__*/ React__default.createElement("div", null, getLabel('installments.modal.payNowMethodDescription'))), /*#__PURE__*/ React__default.createElement("dl", {
        className: "promote-installments-modal-slot__pay-summary"
    }, /*#__PURE__*/ React__default.createElement("dt", null, getLabel('installments.modal.summaryTitle')), /*#__PURE__*/ React__default.createElement("dd", null), /*#__PURE__*/ React__default.createElement("dt", null, getLabel('installments.modal.summaryDescriptionNow')), /*#__PURE__*/ React__default.createElement("dd", null, priceNow), monthsIndexArray.map((index)=>/*#__PURE__*/ React__default.createElement(React__default.Fragment, {
            key: index
        }, /*#__PURE__*/ React__default.createElement("dt", null, getLabel(`installments.modal.summaryDescription${index}`)), /*#__PURE__*/ React__default.createElement("dd", null, pricePerMonth))
    ))));
};

// when enabledProduct is undefined this component will render for every product type
// when enabledProduct is set e.g. ['sunglasses'] it will render ONLY for this product type
const getExcludedProductType = (enabledProductTypes, productType)=>{
    if (!enabledProductTypes || !productType) {
        return false;
    }
    return !enabledProductTypes.some((product)=>product.toLowerCase() === productType.toLowerCase()
    );
};
const PromoteInstallments = ({ siteLogos , numberOfInstallments , minimumPrice , productType , enabledProductTypes , textAlign  })=>{
    var ref;
    const { currency , locale  } = useIntl();
    const { activeVariant  } = useProductData();
    const price = getPrice(activeVariant);
    const { amount: priceAmount  } = ((ref = price.discountedPrice) === null || ref === void 0 ? void 0 : ref.price) || price.originalPrice;
    const excludedProductType = getExcludedProductType(enabledProductTypes, productType);
    const installmentPrice = formatPrice(locale, priceAmount / numberOfInstallments, currency, false, true);
    // conditions to render component
    if (priceAmount < minimumPrice || excludedProductType) {
        return null;
    }
    return /*#__PURE__*/ React__default.createElement(PromoteInstallmentsLabel, {
        installmentPrice: installmentPrice,
        numberOfInstallments: numberOfInstallments,
        textAlign: textAlign,
        modalBodySlot: /*#__PURE__*/ React__default.createElement(PromoteInstallmentsModalSlot, {
            siteLogos: siteLogos,
            price: priceAmount,
            numberOfInstallments: numberOfInstallments
        })
    });
};

function getProductSeoInfo(staticsUrl, activeVariant, product, isBynderImagesEnabled) {
    const title = getProductTitle(activeVariant, product);
    const description = getProductDescription(activeVariant, product);
    const imageUrl = getProductImageUrl(activeVariant, staticsUrl, isBynderImagesEnabled);
    return {
        title,
        description,
        imageUrl
    };
}
function getProductTitle(variant, product) {
    if (variant.metaTitle) {
        return variant.metaTitle;
    } else if (product.metaTitle) {
        return product.metaTitle;
    }
    return product.name || '';
}
function getProductDescription(variant, product) {
    if (variant.metaDescription) {
        return variant.metaDescription;
    } else if (product.metaDescription) {
        return product.metaDescription;
    } else if (variant.description) {
        return variant.description;
    }
    return product.description || '';
}
function getProductImageUrl(variant, staticsUrl, isBynderImagesEnabled) {
    const imageUrls = getImageUrl(variant.media, staticsUrl, isBynderImagesEnabled);
    return `${imageUrls[0].url}?q=70&w=1200&h=630&fit=fill&fm=jpeg`;
}

const mapGraphProductToClientProduct = (serverProduct)=>{
    if ((serverProduct === null || serverProduct === void 0 ? void 0 : serverProduct.__typename) !== 'SunglassesProduct') {
        console.error('This product type is not yet supported');
        return undefined;
    }
    /**
   * TODO: replace this with an actual BE to FE mapper
   *    All this has done is move the problem of casting the BE type to our FE type
   *    which used to be in the product-detail-v2 template to a more visible location.
   *
   *    An appropriate mapper absolutely must be implemented before PDP v2 is put on the opcos.
   */ return swcHelpers.objectSpread({}, serverProduct, {
        recommendedCategoryKey: undefined,
        subscriptionProductSku: undefined
    });
};

const GET_PRODUCT_BY_SLUG = gql`
  query GetProductBySlug($slug: String!, $facets: [SearchFacetInput!]!) {
    getProductBySlug(slug: $slug, facets: $facets) {
      product {
        ... on SunglassesProduct {
          brand
          description
          id
          isReviewEnabled
          name
          isOfferedWithPrescriptions
          metaTitle
          metaDescription
          variants {
            sku
            brandModel
            brandModelColorCode
            description
            frameShape {
              displayName
              shape
            }
            frameMaterial
            metaTitle
            metaDescription
            header
            isAllowBackOrder
            isPolarized
            isReservable
            isGlazeble
            uvRaysProtection
            stockAvailability {
              availableQuantity
              isInStock
            }
            tags {
              key
              value
            }
            targetGender
            frameColor
            lensColor
            media {
              images {
                url
                dimensions {
                  w
                  h
                }
                label
              }
            }
            prices {
              originalPrice {
                currencyCode
                amount
              }
              discountedPrice {
                price {
                  amount
                  currencyCode
                }
                discountInformation {
                  value
                  name {
                    locale
                    value
                  }
                  type
                  description {
                    locale
                    value
                  }
                }
              }
            }
            isVtoEnabled
            isExcludedFromCheckout
            channelAvailability
            usps
            frameType {
              displayName
              type
            }
            opcoBackendId
            erpId
          }
        }
      }
    }
  }
`;

function getProductBySlug(apolloClient, slug, locale) {
    return _getProductBySlug.apply(this, arguments);
}
function _getProductBySlug() {
    _getProductBySlug = swcHelpers.asyncToGenerator(function*(apolloClient, slug, locale) {
        const queryResult = yield apolloClient.query({
            query: GET_PRODUCT_BY_SLUG,
            variables: {
                slug,
                facets: {}
            },
            context: {
                headers: {
                    'Accept-Language': locale
                }
            }
        });
        if (queryResult.error) {
            console.error(queryResult.error);
            throw queryResult.error;
        }
        return mapGraphProductToClientProduct(queryResult.data.getProductBySlug.product);
    });
    return _getProductBySlug.apply(this, arguments);
}

const ProductDetail = ({ children  })=>{
    const { activeVariant: { sku  } , reviewsProvider ,  } = useProductData();
    const { isReviewsEnabled  } = useFeatureToggles();
    return /*#__PURE__*/ React.createElement("div", null, /*#__PURE__*/ React.createElement(Breadcrumbs, {
        pages: []
    }), /*#__PURE__*/ React.createElement(ReviewsProvider, swcHelpers.extends({}, reviewsProvider), /*#__PURE__*/ React.createElement("div", null, /*#__PURE__*/ React.createElement("div", {
        className: "product-detail"
    }, /*#__PURE__*/ React.createElement("div", {
        className: "site-container product-detail__container"
    }, /*#__PURE__*/ React.createElement(ProductTemplate, null)))), children, isReviewsEnabled && /*#__PURE__*/ React.createElement(ReviewDisplay, {
        productId: sku,
        variantId: sku
    })));
};

export { BrandPromotion, CmsProvider, Discount, NonRxProductCTA, ProductDetail, ProductDetailDescription, ProductDetailFeatures, ProductDetailPrice, ProductDetailSlotsProvider, ProductDetailTitle, ProductDetailUsps, ProductDiscountLabel, ProductProvider, PromoteInstallments, PromoteInstallmentsModalSlot, RxGlassesCTA, SelectionBoxRadio, SunglassesRxableSelectContainer as SunglassesRxableSelect, TileLayout, TryOnButton, VariantPicker, VirtualTryOn, VtoConfigProvider, getProductBySlug, getProductSeoInfo, useProductDetailSlots, useVtoConfig, vtoEvent };
//# sourceMappingURL=index.js.map
